import React from 'react';
import { Table, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MonthResponse } from '../../../../services/revenue-report/response/MonthResponse';
import { formatPrice } from '../../../../shared/utils/formatPrice';

interface RevenueTableReportYearProps {
    data: MonthResponse;
    startDate: string;
    endDate: string;
}

const RevenueTableReportYear: React.FC<RevenueTableReportYearProps> = ({ data, startDate, endDate }) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const monthRange: string[] = [];
    let current = new Date(start);
    while (current <= end) {
        monthRange.push(current.toLocaleString("en-US", { month: "long", year: "numeric" }));
        current.setMonth(current.getMonth() + 1);
    }

    const groupedData = data.data.reduce((acc, item) => {
        const date = new Date(item.month_year);
        const monthYear = date.toLocaleString("en-US", { month: "long", year: "numeric" });

        if (!acc[monthYear]) {
            acc[monthYear] = {};
        }
        acc[monthYear][item.room_type_id] = item;
        return acc;
    }, {} as Record<string, Record<string, MonthResponse["data"][0]>>);

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Revenue Report - Yearly", 14, 10);

        const bodyData = data.per_room_type.map(room => [
            room.room_type_name,
            ...monthRange.map(monthYear => {
                const monthData = groupedData[monthYear]?.[room.room_type_id];
                return monthData ? `${formatPrice(monthData.total_revenue)}\nAvg. Rate: ${formatPrice(monthData.avg_revenue)}` : "-";
            }),
            `${formatPrice(room.total_revenue)}\nAvg. Rate: ${formatPrice(room.avg_revenue)}`
        ]);

        // Add total row
        const totalRow = [
            "Total",
            ...monthRange.map(monthYear => {
                const monthData = data.per_month.find(m => new Date(m.month_year).toLocaleString("en-US", { month: "long", year: "numeric" }) === monthYear);
                return monthData ? `${formatPrice(monthData.total_revenue)}\nAvg. Rate: ${formatPrice(monthData.avg_revenue)}` : "-";
            }),
            `${formatPrice(data.per_month.reduce((sum, m) => sum + m.total_revenue, 0))}\nAvg. Rate: ${formatPrice(data.per_month.reduce((sum, m) => sum + m.avg_revenue, 0) / data.per_month.length || 0)}`
        ];

        bodyData.push(totalRow);

        autoTable(doc, {
            head: [["Room Type", ...monthRange, "Year"]],
            body: bodyData,
        });
        doc.save("RevenueReport.pdf");
    };

    const exportToExcel = () => {
        const wsData = [
            ["Room Type", ...monthRange, "Year"],
            ...data.per_room_type.map(room => [
                room.room_type_name,
                ...monthRange.map(monthYear => {
                    const monthData = groupedData[monthYear]?.[room.room_type_id];
                    return monthData ? `${formatPrice(monthData.total_revenue)}\nAvg. Rate: ${formatPrice(monthData.avg_revenue)}` : "-";
                }),
                `${formatPrice(room.total_revenue)}\nAvg. Rate: ${formatPrice(room.avg_revenue)}`
            ])
        ];

        // Add total row
        const totalRow = [
            "Total",
            ...monthRange.map(monthYear => {
                const monthData = data.per_month.find(m => new Date(m.month_year).toLocaleString("en-US", { month: "long", year: "numeric" }) === monthYear);
                return monthData ? `${formatPrice(monthData.total_revenue)}\nAvg. Rate: ${formatPrice(monthData.avg_revenue)}` : "-";
            }),
            `${formatPrice(data.per_month.reduce((sum, m) => sum + m.total_revenue, 0))}\nAvg. Rate: ${formatPrice(data.per_month.reduce((sum, m) => sum + m.avg_revenue, 0) / data.per_month.length || 0)}`
        ];

        wsData.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "RevenueReport.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Room Type</th>
                            {monthRange.map((month, index) => <th key={index}>{month}</th>)}
                            <th>Year</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.per_room_type.map(room => (
                            <tr key={room.room_type_id}>
                                <td>{room.room_type_name}</td>
                                {monthRange.map((monthYear, index) => {
                                    const monthData = groupedData[monthYear]?.[room.room_type_id];
                                    return (
                                        <td key={index} className='text-center'>
                                            {monthData ? (
                                                <>
                                                    {monthData.total_revenue === 0 || monthData.avg_revenue === 0 ? "-" : (
                                                        <>
                                                            <strong>{formatPrice(monthData.total_revenue)}</strong>
                                                            <br />
                                                            Avg. Rate: {formatPrice(monthData.avg_revenue)}
                                                        </>
                                                    )}
                                                </>
                                            ) : "-"}
                                        </td>
                                    );
                                })}
                                <td className='text-center'>
                                    <strong>{formatPrice(room.total_revenue)}</strong>
                                    <br />
                                    Avg. Rate: {formatPrice(room.avg_revenue)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th>Total</th>
                            {monthRange.map((monthYear, index) => {
                                const monthData = data.per_month.find(m => new Date(m.month_year).toLocaleString("en-US", { month: "long", year: "numeric" }) === monthYear);
                                return (
                                    <td key={index} className='text-center'>
                                        {monthData ? (
                                            <>
                                                <strong>{formatPrice(monthData.total_revenue)}</strong>
                                                <br />
                                                Avg. Rate: {formatPrice(monthData.avg_revenue)}
                                            </>
                                        ) : "-"}
                                    </td>
                                );
                            })}
                            <td className='text-center'>
                                <strong>{formatPrice(data.per_month.reduce((sum, m) => sum + m.total_revenue, 0))}</strong>
                                <br />
                                Avg. Rate: {formatPrice(data.per_month.reduce((sum, m) => sum + m.avg_revenue, 0) / data.per_month.length || 0)}
                            </td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default RevenueTableReportYear;